import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { OrderedPicker } from '../../components/ui/OrderedPicker.js';

/**
 * How calls to outside numbers leave this account.
 *
 * Two layers, in the order a call meets them. Prefix routes, when switched on,
 * catch the numbers they name (international via one carrier, say); every other
 * outside number leaves by the account's carriers, tried in order -- one carrier
 * is "everything via that trunk", more is failover.
 *
 * Who may change it follows whose trunks carry the calls: the provider for an
 * account on our trunks, the customer for its own. The server says which
 * (routing.can_edit), so this screen never offers a control the API would refuse.
 */

interface Route {
  id: string;
  tenant_id: string | null;
  name: string;
  match_prefix: string;
  destination_class_id: string | null;
  priority: number;
  trunk_selection: string;
  trunk_ids: string[] | null;
  strip_digits: number;
  prepend: string | null;
  is_enabled: boolean;
}

interface Routing {
  trunk_mode: string;
  prefix_routing: boolean;
  can_edit: boolean;
  default_route: Route | null;
}

interface Trunk {
  id: string; name: string; direction: string; status: string;
  outbound_format: string; carrier_name: string | null; priority: number;
}
interface DestClass { id: string; key: string; name: string; risk_level: string }

export function OutboundRoutesTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Route | 'new' | null>(null);
  const [test, setTest] = useState('');

  const key = ['tenants', tenantId, 'outbound-routes'] as const;
  const routes = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: Route[]; platform_defaults: Route[]; routing: Routing }>(
      `/v1/tenants/${tenantId}/outbound-routes`),
  });
  const options = useQuery({
    queryKey: [...key, 'trunks'] as const,
    queryFn: () => api.get<{ data: Trunk[]; destination_classes: DestClass[] }>(
      `/v1/tenants/${tenantId}/outbound-routes/trunks`),
  });

  const save = useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/outbound-routes/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/outbound-routes`, body),
    onSuccess: () => { setEditing(null); void qc.invalidateQueries({ queryKey: key }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/outbound-routes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const saveRouting = useMutation({
    mutationFn: (body: { default_trunk_ids: string[]; prefix_routing: boolean }) =>
      api.put(`/v1/tenants/${tenantId}/outbound-routing`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (routes.error) return <ErrorBanner error={routes.error} />;

  const rows = routes.data?.data ?? [];
  const defaults = routes.data?.platform_defaults ?? [];
  const routing = routes.data?.routing;
  const canEdit = routing?.can_edit ?? false;
  const prefixOn = routing?.prefix_routing ?? false;
  const trunks = options.data?.data ?? [];
  const trunkName = (id: string) => trunks.find((t) => t.id === id)?.name ?? id.slice(0, 8);

  /**
   * Which carriers a number would try, walking the same order the switch does:
   * the account's prefix routes if switched on (already sorted by the server),
   * then its default, then the platform's. Outside numbers only -- seven digits
   * or more -- because that is all a route may take.
   */
  const digits = test.replace(/[^\d]/g, '');
  const dflt = routing?.default_route ?? null;
  const candidates = [
    ...(prefixOn ? rows.filter((r) => r.is_enabled) : []),
    ...(dflt?.is_enabled ? [dflt] : []),
    ...defaults,
  ];
  const winner = digits.length >= 7
    ? candidates.find((r) => digits.startsWith(r.match_prefix))
    : undefined;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Outbound routing</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            How calls to outside numbers leave this account.
          </p>
        </div>
      </div>

      {options.data && trunks.length === 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            No carrier is linked to this account for outbound calls, so there is
            nothing to route over. Link one on the account's TMS tab first.
          </span>
        </div>
      )}

      {routing && (
        <DefaultRouting
          routing={routing} trunks={trunks}
          pending={saveRouting.isPending}
          onSave={(body) => saveRouting.mutate(body)}
        />
      )}

      {candidates.length > 0 && (
        <div className="arc-card" style={{ marginBottom: 12 }}>
          <div className="arc-card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <label className="arc-label" htmlFor="rt-test">Test a number</label>
            <div className="arc-row" style={{ gap: 10, marginTop: 4 }}>
              <input id="rt-test" className="arc-input arc-mono" style={{ maxWidth: 220 }}
                placeholder="27821234567" value={test}
                onChange={(e) => setTest(e.target.value)} />
              <div style={{ alignSelf: 'center' }}>
                {digits.length < 7 ? (
                  <span className="arc-subtle">An outside number, seven digits or more.</span>
                ) : winner ? (
                  <span className="arc-row" style={{ gap: 7 }}>
                    <Badge tone="ok">{winner.match_prefix ? winner.name : 'Default'}</Badge>
                    <span className="arc-subtle">
                      {winner.match_prefix && <>matches <span className="arc-mono">{winner.match_prefix}</span> · </>}
                      {winner.strip_digits > 0 && <>strips {winner.strip_digits} · </>}
                      {winner.prepend && <>prepends <span className="arc-mono">{winner.prepend}</span> · </>}
                      via {(winner.trunk_ids ?? []).map(trunkName).join(', then ') || 'no carrier'}
                    </span>
                  </span>
                ) : (
                  <span className="arc-badge arc-badge-warn">
                    <Icon name="warning" size={10} /> nothing routes this number, so the call would fail
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {(prefixOn || rows.length > 0) && (
        <div className="arc-card">
          <div className="arc-card-head">
            <h2 className="arc-h2">Prefix routes</h2>
            <span className="arc-subtle">
              {prefixOn ? 'Tried first, longest prefix first' : 'Off: kept, not used'}
            </span>
            <div className="arc-spacer" />
            {canEdit && prefixOn && !editing && (
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing('new')}>
                <Icon name="plus" size={12} /> New route
              </button>
            )}
          </div>

          {editing && (
            <div className="arc-card-body">
              <RouteEditor
                route={editing === 'new' ? null : editing}
                trunks={trunks}
                classes={options.data?.destination_classes ?? []}
                pending={save.isPending}
                onCancel={() => setEditing(null)}
                onSave={(body) => save.mutate(
                  editing === 'new' ? body : { id: editing.id, ...body })}
              />
            </div>
          )}

          {routes.isLoading ? (
            <TableSkeleton rows={4} cols={5} />
          ) : rows.length === 0 ? (
            <Empty icon="carrier" title="No prefix routes"
              hint="Every outside number uses the carriers above. Add a route to send some numbers another way." />
          ) : (
            <div className="arc-table-wrap" style={prefixOn ? undefined : { opacity: 0.55 }}>
              <table className="arc-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>#</th>
                    <th>Route</th><th>Matches</th><th>Number sent</th><th>Carriers</th>
                    {canEdit && <th style={{ width: 80 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} style={r.is_enabled ? undefined : { opacity: 0.5 }}>
                      <td className="arc-num arc-subtle">{i + 1}</td>
                      <td>
                        <div className="arc-row" style={{ gap: 6 }}>
                          <span style={{ fontWeight: 550 }}>{r.name}</span>
                          {!r.is_enabled && <Badge tone="muted">off</Badge>}
                        </div>
                        <div className="arc-subtle" style={{ marginTop: 2 }}>priority {r.priority}</div>
                      </td>
                      <td className="arc-mono">{r.match_prefix}…</td>
                      <td className="arc-mono arc-subtle">
                        {r.prepend ?? ''}
                        {r.strip_digits > 0 ? `…(−${r.strip_digits})` : r.match_prefix}
                        {r.strip_digits === 0 && '…'}
                      </td>
                      <td>
                        {(r.trunk_ids ?? []).length === 0
                          ? <span className="arc-badge arc-badge-warn">none</span>
                          : (r.trunk_ids ?? []).map((id, n) => (
                              <span key={id} className="arc-badge arc-badge-muted" style={{ marginRight: 4 }}>
                                {n === 0 ? '' : 'then '}{trunkName(id)}
                              </span>
                            ))}
                      </td>
                      {canEdit && (
                        <td>
                          <div className="arc-row" style={{ gap: 4 }}>
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              title="Edit" onClick={() => setEditing(r)}>
                              <Icon name="edit" size={12} />
                            </button>
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              title="Delete" onClick={() => remove.mutate(r.id)}>
                              <Icon name="trash" size={12} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/*
        Platform routes apply only to an account with no default of its own.
        Shown because the node applies them: an account with nothing set whose
        calls still connect would otherwise have no explanation.
      */}
      {defaults.length > 0 && (
        <div className="arc-card" style={{ marginTop: 12 }}>
          <div className="arc-card-head">
            <h2 className="arc-h2">Platform routes</h2>
            <span className="arc-subtle">
              {dflt ? 'not used: this account has its own carriers' : 'used until this account has its own carriers'}
            </span>
          </div>
          <div className="arc-card-body">
            <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {defaults.map((d) => (
                <span key={d.id} className="arc-badge arc-badge-muted">
                  <span className="arc-mono">{d.match_prefix || 'all numbers'}</span> {d.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The account's carriers in order, and the prefix routing switch.
 *
 * Both saved together (PUT outbound-routing): "everything via A then B, except
 * by prefix" is one decision, and half of it saved would route calls by a
 * combination nobody chose. The draft resets only when the SAVED values change,
 * not on every refetch, so a window regaining focus cannot wipe an unsaved edit.
 */
function DefaultRouting({ routing, trunks, pending, onSave }: {
  routing: Routing;
  trunks: Trunk[];
  pending: boolean;
  onSave: (body: { default_trunk_ids: string[]; prefix_routing: boolean }) => void;
}) {
  const saved = routing.default_route?.trunk_ids ?? [];
  const savedKey = saved.join(',');
  const [order, setOrder] = useState<string[]>(saved);
  const [prefix, setPrefix] = useState(routing.prefix_routing);
  useEffect(() => {
    setOrder(savedKey ? savedKey.split(',') : []);
    setPrefix(routing.prefix_routing);
  }, [savedKey, routing.prefix_routing]);

  const dirty = order.join(',') !== savedKey || prefix !== routing.prefix_routing;
  const name = (id: string) => trunks.find((t) => t.id === id)?.name ?? id.slice(0, 8);
  const summary = order.length === 0
    ? 'No carriers chosen, so calls to outside numbers have nowhere to go.'
    : order.length === 1
      ? `Every outside call leaves by ${name(order[0]!)}.`
      : `Outside calls leave by ${name(order[0]!)}; if that carrier fails, ${order.slice(1).map(name).join(', then ')}.`;

  return (
    <div className="arc-card" style={{ marginBottom: 12 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Carriers</h2>
        {!routing.can_edit && (
          <span className="arc-subtle">
            {routing.trunk_mode === 'byot' ? 'read-only' : 'set by your provider'}
          </span>
        )}
      </div>
      <div className="arc-card-body">
        <p style={{ margin: '0 0 10px' }}>{summary}</p>
        <TrunkOrder trunks={trunks} value={order} onChange={setOrder} disabled={!routing.can_edit} />
        <p className="arc-hint" style={{ marginTop: 8 }}>
          The next carrier is tried only when a carrier or the network fails — not when the
          number is busy, unanswered or does not exist. A carrier the health checks report as
          down is tried last.
        </p>

        <div className="arc-row" style={{ gap: 9, marginTop: 14 }}>
          <Switch id="rt-prefix" checked={prefix} onChange={setPrefix} disabled={!routing.can_edit} />
          <label htmlFor="rt-prefix" style={{ cursor: routing.can_edit ? 'pointer' : 'default' }}>
            Route some numbers by prefix
            <span className="arc-hint" style={{ display: 'block', marginTop: 2 }}>
              Prefix routes are tried first; anything they do not match uses the carriers above.
            </span>
          </label>
        </div>

        {routing.can_edit && dirty && (
          <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="arc-btn"
              onClick={() => { setOrder(saved); setPrefix(routing.prefix_routing); }}>
              Discard
            </button>
            <button type="button" className="arc-btn arc-btn-primary"
              disabled={order.length === 0 || pending}
              onClick={() => onSave({ default_trunk_ids: order, prefix_routing: prefix })}>
              {pending ? 'Saving…' : 'Save routing'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Carriers in failover order: the position IS the order they are tried.
 * The shared ordered picker, so carriers and codecs rank the same way.
 */
function TrunkOrder({ trunks, value, onChange, disabled = false }: {
  trunks: Trunk[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <OrderedPicker
      items={trunks.map((t) => ({
        id: t.id, label: t.name, detail: `${t.carrier_name ?? '—'} · ${t.outbound_format}`,
      }))}
      value={value} onChange={onChange} disabled={disabled} laterLabel="failover" />
  );
}

function RouteEditor({ route, trunks, classes, pending, onCancel, onSave }: {
  route: Route | null;
  trunks: Trunk[];
  classes: DestClass[];
  pending: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(route?.name ?? '');
  const [prefix, setPrefix] = useState(route?.match_prefix ?? '');
  const [priority, setPriority] = useState(String(route?.priority ?? 100));
  const [strip, setStrip] = useState(String(route?.strip_digits ?? 0));
  const [prepend, setPrepend] = useState(route?.prepend ?? '');
  const [classId, setClassId] = useState(route?.destination_class_id ?? '');
  const [enabled, setEnabled] = useState(route?.is_enabled ?? true);
  const [selected, setSelected] = useState<string[]>(route?.trunk_ids ?? []);

  /**
   * A worked example, updating as the fields change.
   *
   * strip and prepend are the two fields people get wrong, and the mistake is
   * invisible until a carrier rejects the call or -- worse -- connects it to
   * the wrong number. Showing the result removes the guessing.
   */
  const sample = `${prefix}${'5'.repeat(Math.max(0, 11 - prefix.length))}`;
  const stripN = Number(strip) || 0;
  const result = `${prepend}${sample.slice(stripN)}`;

  return (
    <div style={{ marginBottom: 4 }}>
      <h3 className="arc-h2" style={{ marginBottom: 10 }}>
        {route ? `Edit ${route.name}` : 'New prefix route'}
      </h3>
      <div className="arc-grid arc-grid-2" style={{ gap: 10 }}>
        <label className="arc-field">
          <span>Name</span>
          <input className="arc-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="International" />
        </label>
        <label className="arc-field">
          <span>Destination type</span>
          <select className="arc-select" value={classId}
            onChange={(e) => setClassId(e.target.value)}>
            <option value="">Unclassified</option>
            {classes.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}{k.risk_level === 'high' ? ' (high risk)' : ''}
              </option>
            ))}
          </select>
          <div className="arc-hint">Used for fraud alerting thresholds.</div>
        </label>
      </div>

      <div className="arc-grid arc-grid-4" style={{ gap: 10, marginTop: 10 }}>
        <label className="arc-field">
          <span>Matches prefix</span>
          <input className="arc-input arc-mono" value={prefix} inputMode="numeric"
            onChange={(e) => setPrefix(e.target.value)} placeholder="2782" />
          <div className="arc-hint">Digits only, no +.</div>
        </label>
        <label className="arc-field">
          <span>Priority</span>
          <input className="arc-input arc-num" value={priority} inputMode="numeric"
            onChange={(e) => setPriority(e.target.value)} />
          <div className="arc-hint">Only breaks ties between equal-length prefixes.</div>
        </label>
        <label className="arc-field">
          <span>Strip digits</span>
          <input className="arc-input arc-num" value={strip} inputMode="numeric"
            onChange={(e) => setStrip(e.target.value)} />
        </label>
        <label className="arc-field">
          <span>Prepend</span>
          <input className="arc-input arc-mono" value={prepend}
            onChange={(e) => setPrepend(e.target.value)} placeholder="0" />
        </label>
      </div>

      {prefix && (
        <div className="arc-banner arc-banner-info" style={{ marginTop: 10 }}>
          <Icon name="info" size={14} />
          <span>
            A call to <span className="arc-mono">{sample}</span> is sent to the
            carrier as <span className="arc-mono" style={{ fontWeight: 600 }}>{result}</span>.
          </span>
        </div>
      )}

      <div className="arc-field" style={{ marginTop: 14 }}>
        <span className="arc-label">Carriers, in order</span>
        {trunks.length === 0
          ? <span className="arc-subtle">No outbound carrier is linked to this account.</span>
          : <TrunkOrder trunks={trunks} value={selected} onChange={setSelected} />}
      </div>

      <label className="arc-row" style={{ gap: 9, marginTop: 14 }}>
        <Switch id="rt-enabled" checked={enabled} onChange={setEnabled} />
        <span>Enabled</span>
      </label>

      <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="arc-btn arc-btn-primary"
          disabled={!name.trim() || !prefix.trim() || selected.length === 0 || pending}
          onClick={() => onSave({
            name: name.trim(),
            match_prefix: prefix.trim(),
            priority: Number(priority) || 100,
            strip_digits: Number(strip) || 0,
            prepend: prepend.trim() || null,
            destination_class_id: classId || null,
            trunk_ids: selected,
            is_enabled: enabled,
          })}
        >
          {pending ? 'Saving…' : route ? 'Save route' : 'Create route'}
        </button>
      </div>
    </div>
  );
}
